/**
 * The **closed predicate language** — a composable (`and`/`or`/`not`) set of **pure**
 * predicates over (frozen state + recorded inputs). A `modifier`'s `condition` and an `if`
 * atom's `cond` are {@link Predicate}s; the evaluator answers them against a
 * {@link PredicateContext} the interpreter builds from the resolution.
 *
 * **The vocabulary is named RAW idioms (`decisions/0183`).** One op per phrase the rulebook
 * repeats (`is_closest_hostile`, `danger_zone`, `exists_ally`), so an authored clause reads like
 * its rulebook sentence. A new op must name a RAW idiom, and its doc comment quotes the phrase
 * and cites the entry that needed it. Prefer an optional field on an existing op to a sibling op
 * that differs by one fixed argument (`has_status`'s `source` is the model). Nested general forms
 * (a comparator over value expressions, a quantifier with a bound variable) are not ops; needing
 * one is a new decision. Adding an op touches the union, {@link evaluatePredicate}, the zod arm in
 * `schema.ts`, `predicatePhrase.ts`'s wording, and tests. The other sites derive from those.
 *
 * **Positions are read off the live map** (`adjacent_to`, `range_to`, `is_closest_hostile`,
 * `exists_ally`); an unplaced combatant reads `false`. A fact the engine does not model at all is
 * a {@link GmRulingPredicate}, answered by the GM and recorded on the command like a `choose`.
 *
 * The language is **closed** (a discriminated union, not arbitrary expressions): no loops, no
 * data structures, only trivial composition — exactly the ceiling Lancer combat content lives
 * under.
 */

import {
  areaHolds,
  contributesToTerrain,
  encounterEnvironment,
  hasHeatTrack,
  hasStatus,
  hasStressTrack,
  goesDownAndOut,
  hasStructureTrack,
  heldLimitedPool,
  isActor,
  resourceCapRawKey,
  isCharacter,
  objectGroupMembers,
  sideOf,
  spentMove,
  swapFits,
  turnBudgetVirgin,
  worldFor,
  statusNames,
  type AreaGrant,
  type AttackVerb,
  type EarmarkVerb,
  type Cell,
  type Combatant,
  type DamageType,
  type EncounterEnvironment,
  type EncounterState,
  type StatusAddStep,
  type StatusInstance,
} from "@lancer-vtt/domain";
import { NeedsDecisionSignal, type ChoiceSource } from "./choices";
import { compare, type CmpOp } from "./compare";
import { combatantLineOfSight, combatantRange, footprintCells } from "./spatialAnswerer";
// A cycle with `./loader` (which imports `bondmatePartner` back); both are read only inside function bodies.
import { combatantEffectIds } from "./loader";
import type { LedgerCollision, ResolutionLedger } from "./resolutionLedger";
import type { ResourceCap } from "./atoms";
import { affordable } from "../events/gameEvents";

// ── the predicate language (closed, composable) ───────────────────────────────────

/** Logical AND over sub-predicates (empty = vacuously true). */
export type AndPredicate = {
  op: "and";
  preds: Predicate[];
}
/** Logical OR over sub-predicates (empty = vacuously false). */
export type OrPredicate = {
  op: "or";
  preds: Predicate[];
}
/** Logical NOT of a sub-predicate. */
export type NotPredicate = {
  op: "not";
  pred: Predicate;
}

/** A combatant referenced by a predicate, resolved against the context (mirrors AtomActorRef). */
export type PredicateActor = "self" | "target" | "attacker" | (string & {});

/**
 * Which instance's `source` a {@link HasStatusPredicate} requires
 * (`@lancer-vtt/domain`'s `StatusInstance.source`, auto-stamped by `add_status` as the applying
 * combatant unless the atom sets `sourceOverride`). Three shapes, because there are three
 * different things to compare against and none can stand in for another:
 *
 * - **`"carrier"`**: the identity threaded onto this context as
 *   {@link PredicateContext.carrierSource} — "does actor X carry status N whose source is me".
 *   This is how third-party content reaches a combatant it never binds as
 *   `self`/`target`/`attacker`: the closed actor set (`interpret.ts`'s `EffectContext.actors`)
 *   stays closed, and a third combatant is *compared* by the identity already on the mark,
 *   never bound as a new actor slot. `carrierSource` is caller-populated: a consumer that
 *   gathers a clause off a status instance threads that instance's own `source` onto the fired
 *   context, and today exactly two gather shapes do (`lint.ts` rule 4 enforces them). Absent
 *   `carrierSource` reads `false`.
 * - **`{ id }`**: a LITERAL authored value, never a context fact. `"carrier"` cannot tell two
 *   SELF-applied instances of the same status apart: a mech electing Type-I Flight and a mech
 *   Boosting off Rapid Burst Jump Jets both stamp `source` as the same acting combatant
 *   (itself). What differs is which *item* authored the `add_status`, so the entry pins its own
 *   id here and stamps the same id with {@link import("./atoms").AddStatusAtom.sourceOverride}.
 *   `ms_type_i_flight_system` is the worked example.
 * - **`{ actor }`**: the combatant a bound actor ref resolves to — "does the target carry the
 *   Sniper's Mark THIS attacker planted". Resolves through {@link resolveActorId} against the
 *   context's own `actors`, so it works anywhere those are bound, including the attack modifier
 *   fold (which never threads `carrierSource`). An unresolved ref reads `false`.
 *
 * A source-less instance (GM-applied, no `source` stamped) never matches any of the three.
 */
export type StatusSourceSpec = "carrier" | { id: string } | { actor: PredicateActor };

/**
 * True iff the referenced combatant carries `status` (by name, exact). With `source`, only an
 * instance whose `source` matches counts (see {@link StatusSourceSpec}). This one op replaced
 * three sibling ops (`has_status_from_source`/`has_status_from`/`has_status_from_actor`) per
 * `decisions/0183`'s fields-over-siblings rule.
 */
export type HasStatusPredicate = {
  op: "has_status";
  who: PredicateActor;
  status: string;
  source?: StatusSourceSpec;
  /**
   * **`"any_live"`: on anyone still standing** — true iff ANY combatant in the encounter that is
   * not Destroyed carries the status (with `source`, from that source); `who` is ignored. Hunter
   * Lock's "you cannot choose a new target until your current target is destroyed or the scene
   * ends": the lock is held while its mark sits on a live target. Omitted → `who`'s own statuses.
   */
  scope?: "any_live";
  /**
   * **`"this_turn"`: only an instance applied before the turn in progress** (its stamped
   * `appliedTurn` is not the current `turnOrdinal`). The Kraul Rifle's "during their next turn":
   * an impale landed during the target's own turn (an Overwatch shot) waits for its following turn.
   * An instance with no stamp counts. Ignored with `scope: "any_live"`.
   */
  appliedBefore?: "this_turn";
};

/**
 * The closed **capability** vocabulary a {@link HasCapabilityPredicate} names — mirrors
 * {@link CAPABILITY_PREDICATES}'s keys
 * exactly (`schema.test.ts`'s parity check keeps the two lock-step). Adding a capability
 * (e.g. `sensors`) once it has a code-side predicate is a one-line addition to both this
 * union and {@link CAPABILITY_PREDICATES}, never a schema reshape.
 */
export type Capability = "heatTrack" | "structureTrack" | "stressTrack" | "terrain" | "downAndOut" | "character";

/**
 * True iff the referenced combatant carries the named **capability** — the sanctioned
 * authoring-DSL replacement for the `stat maxHeat`-style null-sentinel workaround the
 * authoring playbook warns against ("Pick the lane"). Dispatches on **capability presence**,
 * never `kind`; the hard rule is that no kind/classifier predicate belongs in the authoring
 * schema, ever. `has_capability` delegates to the SAME
 * code-side predicates (`hasHeatTrack`/`hasStructureTrack`/`hasStressTrack`/
 * `contributesToTerrain`/`goesDownAndOut`, `@lancer-vtt/domain`) rules code dispatches on — one source of
 * truth per capability, read through {@link CAPABILITY_PREDICATES}.
 */
export type HasCapabilityPredicate = {
  op: "has_capability";
  who: PredicateActor;
  capability: Capability;
}

/**
 * True iff the referenced combatant's frozen effect ids include `id` exactly: the merged
 * `pilotEffectIds`/`frameEffectIds`/`pilotGearEffectIds`/`npcFeatureEffectIds` lists the gather
 * walks (`combatantEffectIds`, `loader.ts`). For a clause whose carrier is not the entity that
 * grants it: the `Bondmate` status's Intercede clause gates on the protector holding
 * `t_bonded:2`. Exact match, so a talent rank is named with its rank suffix.
 */
export type HasEffectIdPredicate = {
  op: "has_effect_id";
  who: PredicateActor;
  id: string;
}

/**
 * True iff the resolution's **attacker** is the referenced combatant (Type-3 Projected
 * Shield) — most useful as `attacker_is: "partner"` (is the combatant attacking me my bound
 * partner?). `who` resolves through the same {@link PredicateActor} vocabulary, including the
 * `"partner"` keyword (the modifier's own bound `StatusInstance.partner`).
 */
export type AttackerIsPredicate = {
  op: "attacker_is";
  who: PredicateActor;
}

/**
 * True iff the resolution's **target** is the referenced combatant (Type-3 Projected
 * Shield) — most useful as `target_is: "partner"` (am I
 * attacking my bound partner?). The `attacker_is` sibling for the other direction of a
 * bound pair.
 */
export type TargetIsPredicate = {
  op: "target_is";
  who: PredicateActor;
}

/** True iff the named resource on the referenced combatant compares to `value`. */
export type ResourcePredicate = {
  op: "resource";
  who: PredicateActor;
  key: string;
  cmp: CmpOp;
  value: number;
}

/**
 * True iff the charges left in the Limited pool the referenced combatant holds for the first of
 * `items` it carries one for compare to `value` (`heldLimitedPool`). False when it holds none.
 * Walking Armory's Ammo Case offers a round only when the case can pay for it.
 */
export type LimitedChargesPredicate = {
  op: "limited_charges";
  who: PredicateActor;
  items: string[];
  cmp: CmpOp;
  value: number;
}

/**
 * True iff `who` still has a use of the cadence cap `cap` (`event-engine-costs` 6.1), counted per
 * `on` when given (RAW "1/scene on each character"), net of the uses pending in the resolution.
 * The `use_cap` atom spends it.
 */
export type CapOpenPredicate = {
  op: "cap_open";
  who: PredicateActor;
  cap: ResourceCap;
  on?: PredicateActor;
}

/** True iff a frozen base stat compares to `value` (`heat >= maxHeat/2` is Danger Zone, derived). */
export type StatPredicate = {
  op: "stat";
  who: PredicateActor;
  /**
   * A readable stat: `hp`/`heat`/`armor`/`evasion`/`edef`/`speed`/`saveTarget`/`maxHp`/`size`/
   * `maxHeat`. `saveTarget` was spelled `save` until 2026-08-28; it always read the frozen
   * save TARGET (the DC this combatant imposes), so the rename is a correction of the label,
   * not a change of what is read. It is a STAT read, never a save roll bonus — for that go
   * through `saveBonusFor(c, axis)` (`decisions/0163`).
   */
  stat: "hp" | "heat" | "armor" | "evasion" | "edef" | "speed" | "saveTarget" | "maxHp" | "maxHeat" | "size";
  cmp: CmpOp;
  value: number;
}

/**
 * **A yes/no fact the GM rules on**, for a condition the engine does not model (`decisions/0183`).
 * `t_tactician:2`'s "while you are at a higher elevation than your target": elevation is not in
 * game state, so no op can answer it. A positional fact the map CAN answer is a geometry op
 * (`adjacent_to`, `range_to`), never this.
 *
 * The answer rides the resolution's {@link ChoiceSource} ({@link PredicateContext.rulings}) under
 * the prompt id `gm_ruling:<id>`, with options `yes`/`no`, the same round-trip a `choose` uses.
 * `id` is scoped per entry at load (`<entryId>/<id>`, `loader.ts`'s `loadOverlay`), so two
 * entries can't share an answer by picking the same id. With no recorded answer an interactive
 * resolution **halts** and asks the GM ({@link NeedsDecisionSignal}, `who` = {@link GM_DECIDER});
 * a replay or fixture reads the recorded answer and never halts, and a missing one reads `false`.
 *
 * `prompt` is the question the GM sees, worded from RAW, with `{self}`/`{target}`/`{attacker}`/
 * `{partner}` replaced by combatant names when it is asked.
 */
export type GmRulingPredicate = {
  op: "gm_ruling";
  id: string;
  prompt: string;
};

/**
 * True iff the referenced combatant's **loadout** carries a weapon of the named **class**
 * (t_siege_specialist:1's "If you have a Cannon" gate on Jackhammer Round). **A
 * loadout-eligibility check, independent of
 * any attack in progress** — it works on a standalone `action`-kind clause's `condition`
 * where no "firing weapon" concept exists at all (unlike the native `match.weaponType`
 * attack-family fact, `family.ts`'s `AttackMatch`, which reads the resolution's *firing*
 * weapon and is only populated mid-attack). Scans `who`'s frozen `base.weapons` directly for
 * ANY weapon whose `WeaponProfile.type` matches `value` verbatim (the same PC-mech-weapon-only
 * field the attack-family `weaponType` match reads — pilot-gear/NPC weapons never carry it, so
 * this predicate is mech-loadout-only by the same construction). No positional/current-attack
 * facts read.
 */
export type LoadoutHasWeaponTypePredicate = {
  op: "loadout_has_weapon_type";
  who: PredicateActor;
  value: string;
}

/**
 * True iff there **exists an allied combatant** (same {@link "@lancer-vtt/domain".sideOf
 * side} as `who`, excluding `who` itself) satisfying a spatial relation measured against
 * `relativeTo` (tactician's "at least one allied character is Engaged with your target").
 * The one **existential-quantified** predicate in the language: every other op reads a fact
 * about a *single* bound actor pair; this one asks
 * "does at least one roster member matching a filter satisfy a condition," so it iterates
 * `ctx.state.combatants` directly rather than resolving one `who`.
 *
 * **Reads REAL map geometry.** It calls {@link "./spatialAnswerer".combatantRange} directly,
 * the SAME footprint-to-footprint metric `adjacent_to`/`range_to` use, never a hand-rolled
 * distance. A candidate with no map position (`combatantRange` returns `null`) never satisfies
 * the relation.
 *
 * `who` names whose ally-group to search (`self`/`attacker`/`target`/a raw id); `relativeTo`
 * names the actor each candidate's spatial relation is measured against — tactician is
 * `who: "attacker"`, `relativeTo: "target"` ("an ally is Engaged **with your target**," not
 * with the attacker). `rel: "adjacent"` is footprint-adjacency
 * (Engaged is modeled that way throughout); `rel: "range"` takes a `param` threshold and reads
 * `false` for every candidate when `param` is omitted (no threshold to compare). **`who` is NEVER counted as
 * its own ally** — RAW's "at least one allied character" reads as someone else, not a
 * reflexive self-match; the roster scan explicitly skips `who`'s own id.
 */
export type ExistsAllyPredicate = {
  op: "exists_ally";
  who: PredicateActor;
  rel: "adjacent" | "range";
  relativeTo: PredicateActor;
  param?: number;
  /**
   * Who counts. Omitted or `"ally"`: `who`'s own side. `"character"`: any character on either side
   * (Logic Bomb's "not adjacent to any character"). `who` itself never counts either way.
   */
  among?: "ally" | "character";
}

/**
 * True iff the referenced combatant is in the **Danger Zone** — current Heat at or
 * above half its Heat capacity (RAW: a character is in the Danger Zone when `heat >=
 * maxHeat/2` — nuclear_cavalier's "while in the Danger Zone"). A **dedicated op, not a
 * {@link StatPredicate} extension**: `stat`'s `cmp`/
 * `value` only compares a stat to an authored **literal** `value: number` — it can never
 * compare one stat to *another* stat, and the Danger Zone threshold (`maxHeat / 2`) is a
 * **per-combatant derived value** (a frame's heat cap varies), not a number an author can
 * write down once. Stat/resource values are literals or `{VAL}`, never another stat — so
 * `stat`'s doc-comment aside ("`heat >= maxHeat/2` is Danger Zone, derived") names the RULE,
 * not a literal `stat` clause that could express it. No heat track (`c.base.maxHeat`/`c.current.heat`
 * absent, i.e. `!hasHeatTrack(c)`) never satisfies (reads `false`) — mirrors {@link
 * StatPredicate}'s own `readStat` absent-means-`null` convention. **Not** the seed
 * `"Danger Zone"` status (`seedEntries.ts`) — that entry is advisory-only and is never
 * `add_status`'d by anything, so `has_status` on it would silently never fire; this
 * predicate reads the live heat/maxHeat pair directly, the actual gate.
 */
export type DangerZonePredicate = {
  op: "danger_zone";
  who: PredicateActor;
}

/**
 * True iff it is `who`'s own turn: the activation is `acting` and `who` is its actor. RAW's "on
 * your turn" (Duelist 1, Gunslinger 1, Exemplar 1, Nuclear Cavalier 1–2): an attack made on
 * someone else's turn, an Overwatch above all, doesn't qualify.
 *
 * No turn in progress (`activation.turn.phase === "idle"`: combat not sequenced, or between turns)
 * reads `true`. Nobody's turn is running, so there is no "someone else's turn" to exclude, and a
 * clause keeps working in unsequenced play the way it did before this op. An unresolvable `who`
 * reads `false`.
 */
export type OnTurnPredicate = {
  op: "on_turn";
  who: PredicateActor;
}

/**
 * True iff `who` is the acting unit and has spent movement this turn: its own voluntary moves,
 * read off the turn's spend counter (`spentMove`). Boosting without moving, and being pushed or
 * pulled, are not moving. Not `who`'s turn (or no turn running) reads `false`. Spotter's
 * Panopticon ("if you did not move") is the first reader.
 */
export type MovedThisTurnPredicate = {
  op: "moved_this_turn";
  who: PredicateActor;
}

/**
 * True iff `who` is the acting unit and has taken any action or movement this turn: it spent an
 * action point or a move point (the turn's spend counter), or used any action, free and Protocol
 * actions included (the per-turn `actionUses`/`protocolUses` ledgers). A reaction taken on its own
 * turn spends neither, so it doesn't count. Not `who`'s turn (or no turn running) reads `false`.
 * The Kraul Rifle's reel-in ("any time after your target takes any action or movement during their
 * next turn") is the first reader.
 */
export type ActedThisTurnPredicate = {
  op: "acted_this_turn";
  who: PredicateActor;
}

/**
 * True iff a push this resolution drove `who` into something and stopped short
 * (`ResolutionLedger.collisions`): `with: "character"` when another character occupied the space,
 * `with: "obstruction"` when an obstruction stopped it. Juggernaut's Kinetic Mass Transfer reads
 * both. No push yet, or no ledger in scope, reads `false`.
 */
export type CollidedPredicate = {
  op: "collided";
  who: PredicateActor;
  with: "character" | "obstruction";
}

/**
 * True iff `who` is **hostile to** (on a different {@link "@lancer-vtt/domain".sideOf
 * side} than) `relativeTo` (ms_stasis_generator/ms_stay_of_execution's "Hostile characters
 * can succeed on a save to ignore this effect; a willing ally gets none"). **The same-side
 * comparison exposed as its own arm:** {@link ExistsAllyPredicate} computes
 * `sideOf(candidate) !== side` internally to filter its roster scan; this op surfaces that
 * fact as a standalone predicate an author can gate a `force_save` on — a **pairwise** side
 * comparison (mirrors
 * {@link AttackerIsPredicate}/{@link TargetIsPredicate}'s two-actor-ref shape), not
 * `exists_ally`'s roster-wide existential — the RAW clause asks "is THIS chosen target
 * hostile to me," not "does some hostile combatant exist somewhere." Either ref missing
 * a live combatant (an unresolvable actor) reads `false` — the same closed-language
 * absent-means-false convention every other predicate here uses, never a throw.
 */
export type HostileToPredicate = {
  op: "hostile_to";
  who: PredicateActor;
  relativeTo: PredicateActor;
}

/**
 * True iff `who` is currently footprint-adjacent (real map distance == 1) to `relativeTo`
 * (t_bonded:1's self-benefit "Any mech skill checks and saves ... makes while you are adjacent
 * [to your Bondmate] gain +1 Accuracy"). Either side can name any bound actor, a dynamically
 * bound `partner` (a Bondmate) included. {@link ExistsAllyPredicate} is existential over a whole side, not a single named pair. This
 * op instead reads REAL map geometry directly via {@link "./spatialAnswerer".combatantRange}
 * — the SAME footprint-to-footprint metric {@link ExistsAllyPredicate}/{@link
 * HostileToPredicate}'s pairwise cousins already reuse — so it needs no `ctx.spatial`
 * recorded-input plumbing at all, unlike `spatial` itself.
 *
 * `who: "self", relativeTo: "partner"` is the shape t_bonded:1 authors. `ctx.actors.partner`
 * is populated the SAME way {@link AttackerIsPredicate}/{@link TargetIsPredicate}'s own
 * `who:"partner"` already reads it — {@link import("./modifiers").foldModifiers}'s per-modifier
 * merge of a `BoundModifier.partner` — here threaded onto the pilot-effect gather via a
 * `partner: bondmatePartner(state, carrierId)` kwarg at the three save/check fold sites
 * (`interpretForceSaveRoll`, `saves.ts`'s `save_ends`, `contest.ts`'s opposed check),
 * mirroring `grapplePartner`'s own `pilot_modifier_partner_binding` precedent exactly — a
 * shipped seam reused, not new vocabulary. Either ref
 * unresolved (no live combatant, e.g. no Bondmate held, or the partner is unplaced) reads
 * `false` — the closed absent-means-false convention every predicate here uses.
 */
export type AdjacentToPredicate = {
  op: "adjacent_to";
  who: PredicateActor;
  relativeTo: PredicateActor;
}

/**
 * True iff `who`'s Size compares to `relativeTo`'s by `cmp` (Beckon: "Your target must ... be the
 * same Size as you or larger" is `{ who: "target", cmp: ">=", relativeTo: "self" }`). Either
 * combatant unknown reads `false`.
 */
export type SizeVsPredicate = {
  op: "size_vs";
  who: PredicateActor;
  cmp: CmpOp;
  relativeTo: PredicateActor;
}

/**
 * True iff `who` and `relativeTo` can swap places, each footprint landing legally on the other's
 * anchor ({@link swapFits}, the same fit the `teleport { swap }` atom moves by). Either one
 * unknown or unplaced reads `false`.
 */
export type CanSwapPredicate = {
  op: "can_swap";
  who: PredicateActor;
  relativeTo: PredicateActor;
}

/**
 * True iff `who` has line of sight to `relativeTo` on the live map ({@link
 * import("./spatialAnswerer").combatantLineOfSight}, the geometry Intercede's protector gate and
 * the activate reach wall use). For an action whose RAW names "a character within line of sight"
 * but no range, so `checkActivateUnitReach` has no frozen reach to gate on (Type-3 Projected
 * Shield). Either combatant unknown or unplaced reads `false`.
 */
export type LineOfSightPredicate = {
  op: "line_of_sight";
  who: PredicateActor;
  relativeTo: PredicateActor;
}

/**
 * True iff `who` is adjacent to a placed, not Destroyed drone or deployable: a drone (a non-actor
 * character), or anything a system deployed (`deployedFrom`: a Jericho wall, a turret group),
 * whoever owns it. A mine never counts; neither does a map object no system deployed. PUPPETMASTER's Electropulse, "adjacent to any Drone or Deployable, even
 * those they own" (`ll1-drones` 7.1).
 */
export type AdjacentToDeployedPredicate = {
  op: "adjacent_to_deployed";
  who: PredicateActor;
}

/**
 * True iff `who`'s footprint distance to `relativeTo` falls within `[min, max]` (both
 * inclusive, either or both omitted) — Siege Armor's "Resistance to all damage from attacks
 * that originate beyond range 3" (`npc-features-fielded-roster` Phase 2, `decisions/0171`),
 * authored `range_to(attacker, self, min: 4)`. **Live geometry, the {@link HostileToPredicate}/
 * {@link AdjacentToPredicate} precedent** — reads {@link "./spatialAnswerer".combatantRange}
 * directly, never the GM-recorded-boolean `spatial` seam (a "beyond range N" fact is exactly as
 * pre-answerable as adjacency, i.e. not at all — see {@link AdjacentToPredicate}'s own doc for
 * why a THIRD dynamically-bound pairwise fact can't ride the fixed self/target `spatial`
 * recording). `combatantRange` returning `null` (either actor unplaced/unresolvable) reads
 * `false` — the same closed absent-means-false convention every predicate here uses. Omitting
 * both `max`/`min` is a vacuous "any range" gate (always `true` once both actors resolve and
 * have a live position) — an author error to catch by review, not something this predicate
 * rejects (mirrors `exists_ally`'s own `param`-omitted-reads-false choice being a **content**
 * discipline, not enforced by the type).
 */
export type RangeToPredicate = {
  op: "range_to";
  who: PredicateActor;
  relativeTo: PredicateActor;
  /** `"sensors"` is `relativeTo`'s frozen Sensors ("an allied character within Sensors"); none reads `false`. */
  max?: number | "sensors";
  min?: number;
}

/**
 * True iff `who` is the (optionally STRICTLY unique) closest hostile character to `relativeTo`
 * — Point-Defense Shield's "Resistance to all damage from the closest hostile character; ...
 * If multiple characters are equally close, this effect does not apply" (Core p.297,
 * `npc-features-fielded-roster` Phase 2, `decisions/0171`), authored
 * `is_closest_hostile(attacker, self, unique: true)`. Live geometry over EVERY hostile-to-
 * `relativeTo` CHARACTER ({@link import("@lancer-vtt/domain").isCharacter} — a hostile mine,
 * deployable or object is not a character and never counts, as `who` or as a rival) with a
 * resolvable {@link "./spatialAnswerer".combatantRange} distance
 * to it (an unplaced hostile can never be "closest" — it contributes no measurable distance and
 * is simply excluded from the comparison, the same silent-exclusion shape {@link
 * ExistsAllyPredicate} uses for an unplaced candidate). `who` must itself be hostile to
 * `relativeTo` (mirrors {@link HostileToPredicate}'s own side check) AND have a resolvable
 * distance to it; if no hostile has one, or `who` doesn't, the result is `false`.
 *
 * **`unique: true`** (PDS's own RAW shape) — `who` must be the SOLE hostile achieving the
 * minimum distance; a tie at the minimum (two hostiles equally close) makes the predicate
 * `false` for EVERY hostile, `who` included, per the RAW carve-out quoted above.
 * **`unique: false`** — `who` need only be AT the minimum distance, ties included (any
 * shipped content wanting "one of the nearest, ties allowed" rather than PDS's strict
 * uniqueness). Either ref failing to resolve to a live combatant reads `false` — the closed
 * absent-means-false convention every predicate here uses.
 */
export type IsClosestHostilePredicate = {
  op: "is_closest_hostile";
  who: PredicateActor;
  relativeTo: PredicateActor;
  unique: boolean;
}

/**
 * True iff `who`'s footprint lies at least partly inside a live standing area (`AreaEffect`) whose
 * `source` is `source`, optionally only one that grants `grants`. Blinkfield Intervention's
 * "a character or object within the affected area" is `in_area(target, self, outside_attack_difficulty)`:
 * the target is under the carrier's own dome, not a smoke cloud she threw. Live geometry, like
 * {@link RangeToPredicate}; an unplaced `who` or an unresolved `source` reads `false`.
 */
export type InAreaPredicate = {
  op: "in_area";
  who: PredicateActor;
  source: PredicateActor;
  grants?: AreaGrant;
}

/**
 * True iff `who` currently has a LIVE deployed object group named `groupId` in play
 * (ms_pattern_a_jericho_deployable_cover). Reads {@link
 * import("@lancer-vtt/domain").objectGroupMembers} against `` `${who}:${groupId}` `` — the
 * SAME id scheme {@link import("./atoms").DeployObjectAtom}/{@link
 * import("./atoms").RecallObjectAtom} mint/despawn against — and is `true` iff that query
 * returns at least one member (live OR Destroyed; a Destroyed section still occupies the
 * group's ids until recalled, see {@link import("./atoms").RecallObjectAtom}'s own doc).
 *
 * **Purpose: lets ONE overlay entity id toggle between deploy and recall.**
 * `resolveActivate`'s `gatherActions` merges every `kind:"action"` clause under one entity
 * id into a SINGLE activation (there is no per-clause sub-selection a player can aim at) —
 * so a system offering two RAW-distinct player affordances (Deploy vs. Recall) under one
 * compendium id needs them expressed as ONE clause that branches, not two independent
 * clauses that would both fire together. `if{cond: object_group_deployed, then: [recall_object],
 * else: [deploy_object]}` is exactly that: not-yet-deployed → deploy; already-deployed →
 * recall. `who` missing a live combatant reads `false` (the closed absent-means-false
 * convention every predicate here uses).
 */
export type ObjectGroupDeployedPredicate = {
  op: "object_group_deployed";
  who: PredicateActor;
  groupId: string;
  /**
   * `true` (`ll1-drones` D1): count only members still on the board and not Destroyed. A drone's
   * deploy/recall toggle reads this, so a shot-down drone (unplaced by the destroy sweep, its system
   * destroyed) reads as not deployed and the row deploys afresh once the system is repaired. Absent
   * → any member, wreckage included (Jericho, whose Destroyed sections are repaired in place).
   */
  live?: true;
}

/** Is the `groupId` group out? Every member, or with `live` only placed, non-Destroyed ones — see {@link ObjectGroupDeployedPredicate.live}. */
export function objectGroupOut(state: EncounterState, groupId: string, live?: boolean): boolean {
  const members = objectGroupMembers(state, groupId);
  return live === true ? members.some((m) => m.pos != null && !hasStatus(m.statuses, "Destroyed")) : members.length > 0;
}

/**
 * True iff the encounter's current environment/hazard tag equals `value` (ms_eva_module's
 * "in a low or zero gravity or underwater
 * environment"). Reads {@link "@lancer-vtt/domain".EncounterState.environment} directly off
 * `ctx.state` via {@link "@lancer-vtt/domain".encounterEnvironment} — **no threaded
 * per-resolution fact needed** (unlike the native attack-family `match` facts, which read a
 * fact about the in-progress *resolution*), this reads a standing *scene* fact any clause can
 * consult regardless of whether an attack is in progress — the same posture as {@link
 * DangerZonePredicate}, which also reads `ctx.state` directly). A content author combines two
 * of these with `or` for "zero-g OR underwater"
 * (EVA Module's exact gate) — the closed language's existing composition, no new combinator
 * needed. No `who` operand: the environment is scene-wide, not per-combatant.
 */
export type SceneEnvironmentPredicate = {
  op: "scene_environment";
  value: EncounterEnvironment;
}

/**
 * True iff a `choose` atom's `prompt` **actually resolved to** the named option id, THIS
 * SAME resolution (t_walking_armory:1's ammo rounds). Reads the
 * {@link import("./resolutionLedger").ResolutionLedger}
 * a `choose` atom writes to the instant it decides (`interpretChoose`) — NOT the raw
 * `intent.choices` map, so a `choose` that silently took its first-option default (no
 * recorded answer) still reads correctly here. `key` names the `choose` atom's own
 * `prompt` (the same string {@link import("./atoms").ChooseAtom.prompt} carries); `value`
 * is the expected option id. No `who` operand — this reads a fact about the resolution
 * itself, threaded onto {@link PredicateContext.chosen} by every `EffectContext`/
 * `FireContext` this resolution constructs (see {@link import("./resolutionLedger").
 * ResolutionLedger}'s own doc for the gap this closes). Absent (no `choose` has fired yet
 * under this `key`, or this isn't an attack resolution at all — a save/contest/lifecycle
 * point) reads `false` for every `value`, the same absent-means-false convention every
 * other predicate here uses.
 */
export type ChosePredicate = {
  op: "chose";
  key: string;
  value: string;
}

/**
 * One predicate. Closed + composable; spatial predicates resolve as recorded input. The union
 * is extensible (a new arm is additive).
 */
export type Predicate =
  | AndPredicate
  | OrPredicate
  | NotPredicate
  | HasStatusPredicate
  | HasEffectIdPredicate
  | HasCapabilityPredicate
  | ResourcePredicate
  | LimitedChargesPredicate
  | CapOpenPredicate
  | StatPredicate
  | GmRulingPredicate
  | AttackerIsPredicate
  | TargetIsPredicate
  | LoadoutHasWeaponTypePredicate
  | ExistsAllyPredicate
  | DangerZonePredicate
  | OnTurnPredicate
  | MovedThisTurnPredicate
  | ActedThisTurnPredicate
  | CollidedPredicate
  | SceneEnvironmentPredicate
  | HostileToPredicate
  | AdjacentToPredicate
  | AdjacentToDeployedPredicate
  | SizeVsPredicate
  | CanSwapPredicate
  | LineOfSightPredicate
  | RangeToPredicate
  | IsClosestHostilePredicate
  | InAreaPredicate
  | ObjectGroupDeployedPredicate
  | ChosePredicate;

/**
 * The ONE map from a {@link Capability} key to the code-side predicate it delegates to —
 * every reader of "does this combatant have a heat track" (code AND authored content)
 * ultimately bottoms out here. Adding a capability
 * that already has a code predicate (e.g. a future `sensors: hasSensors`) is a one-line
 * entry; `schema.test.ts`/`coverage.test.ts` keep the DSL enum, this map, and the
 * interpreter's implemented-ops list in lock-step.
 */
export const CAPABILITY_PREDICATES: Record<Capability, (c: Combatant) => boolean> = {
  heatTrack: hasHeatTrack,
  structureTrack: hasStructureTrack,
  stressTrack: hasStressTrack,
  terrain: contributesToTerrain,
  downAndOut: goesDownAndOut,
  character: isCharacter,
};

// ── evaluation context ────────────────────────────────────────────────────────────

/**
 * The facts a {@link Predicate} evaluates against: the frozen {@link EncounterState},
 * the bound actor ids (`self`/`target`/`attacker` → combatant ids), and the recorded
 * inputs (a `gm_ruling`'s answer source, the `choose` ledger) that make it replay the same.
 */
export type PredicateContext = {
  state: EncounterState;
  /** Resolve `self`/`target`/`attacker` (and a raw id) to a combatant id. */
  actors: {
    self?: string;
    target?: string;
    attacker?: string;
    /**
     * The **bound partner** of the modifier/clause currently being evaluated — resolved by the
     * `"partner"` {@link
     * PredicateActor} keyword. Populated per-clause (a carrier can hold several
     * relationship statuses with different partners), NOT once for the whole resolution —
     * `foldModifiers`/the protector-gather compiler set it to the specific `BoundModifier`/
     * `Handler`'s own `partner` before evaluating that clause's condition.
     */
    partner?: string;
  };
  /**
   * Where a {@link GmRulingPredicate} reads its answer: the resolution's own {@link ChoiceSource}
   * (the same one its `choose` atoms read). An interactive source (`requireAnswer`) halts on a
   * missing answer; a recorded one reads it or `false`. Absent → every `gm_ruling` reads `false`.
   */
  rulings?: ChoiceSource;
  /**
   * The typed **per-family event payload** — ONE typed record per firing. Set by {@link
   * predicateContextFrom} (projected off {@link import("./interpret").EffectContext
   * }`.event`, itself threaded from {@link import("./triggerBus").FireContext}`.event`
   * at the fire site) and by the raw-attack `attackPredicateContext` builder
   * (`modifiers.ts`), which builds an {@link AttackEvent} directly from its own
   * kwargs. Absent (a save/contest/lifecycle point with no event fact in scope) reads
   * as no match on every event-fact predicate op below — the same absent-means-false
   * convention every other threaded fact here uses.
   */
  event?: TriggerEvent;
  /**
   * The resolution-scoped **fact ledger's** `choose` record —
   * a `prompt` → resolved-option-id map, set by {@link ChosePredicate}'s one write site
   * (`interpret.ts`'s `interpretChoose`) and projected onto every `PredicateContext` this
   * resolution builds (`predicateContextFrom`, `attackPredicateContext`). Absent (a
   * save/contest/lifecycle point, or no `choose` has fired yet this resolution) reads as
   * no match for every `chose` value. See {@link import("./resolutionLedger").
   * ResolutionLedger} for why this needs to be a mutable, by-reference-threaded fact
   * rather than a frozen one. Stays context-level, not an event fact.
   */
  chosen?: Record<string, string>;
  /** The resolution-scoped ledger's push collisions (`ResolutionLedger.collisions`), read by `collided`. */
  collisions?: readonly LedgerCollision[];
  /** The action's pending costs (the resolution ledger), so a capped modifier's affordable check counts them (`events/gameEvents.ts` `affordable`). */
  costs?: ResolutionLedger;
  /**
   * The **firing clause's own carrying-instance's source** — set by a caller that gathered
   * the currently-firing clause off a `StatusInstance` carrying a `source`, from that SAME
   * instance's `source` field. Read by `has_status` with `source: "carrier"` ({@link StatusSourceSpec}) as the "me"
   * identity to compare a scanned instance's own `source` against. Absent (every caller that
   * isn't gathering off a sourced status instance, or an instance with no `source` stamped)
   * reads as no match. Stays context-level, not an event fact.
   */
  carrierSource?: string;
}

// ── typed per-family event payloads ────────────────────────────────────────────────

/**
 * The **cover degree** an attack's target stands in, as the attack spine's own cover fold
 * ({@link import("../resolve/attackModifiers").foldAttackCover}) settles it: RAW's two degrees
 * plus an explicit `"none"`. Mirrors that fold's `CoverFold.degree` union exactly rather than
 * re-deriving it — a THREE-value enum, never a boolean, because "no cover" and "soft cover" are
 * different rules answers and a boolean would collapse them (Sniper's Mark, Core p.309, is
 * ignored by cover of EITHER degree, but a future clause that cares only about hard cover needs
 * the distinction preserved).
 *
 * Kept here beside {@link AttackEvent} rather than imported from `resolve/` so the effects
 * vocabulary has no dependency on the attack spine (the spine imports the vocabulary, not the
 * other way round).
 */
export type CoverDegree = "none" | "soft" | "hard";

/**
 * The attack-family event payload — every attack-scoped fact as one typed record. Carried on
 * every attack-related `PredicateContext`: the declared fire (pre-roll — `hit`/`crit`/`rawD20`/
 * `natural20` are `undefined`, the outcome isn't known yet), the resolved hit/crit/miss fire
 * (post-roll — every field set), and the attacker/target modifier folds (`resolve/attackModifiers.ts`'s
 * `atkMods`/`tgtMods`, and a roll replacement's facts (`events/rollReplacements.ts`), via {@link
 * import("./modifiers").attackPredicateContext}). Booleans, not a 3-state outcome enum (that
 * shape is an authoring trap): **`crit` implies `hit`** (RAW — a crit IS a hit), so
 * `{ hit: false, crit: true }` is never constructed; "on miss" reads as `hit === false`.
 * `weaponType`/`mount` are `undefined` for a tech attack (no mech weapon in play).
 */
export type AttackEvent = {
  family: "attack";
  /**
   * Pre-roll (the declared fire) vs. post-roll (the unified hit/crit/miss resolved fire) —
   * the field `AttackMatch.phase` reads to distinguish a declared-only clause from a
   * resolved-only one (double-fire avoidance). Set at the fire sites, inferred from whether
   * `natural20`/`hit`/`crit` are known.
   */
  phase?: "declared" | "resolved" | "after";
  hit?: boolean;
  crit?: boolean;
  /** The raw (unmodified) to-hit d20 roll. `undefined` pre-roll. */
  rawD20?: number;
  /** `rawD20 === 20`. `undefined` pre-roll. */
  natural20?: boolean;
  ranged?: boolean;
  tech?: boolean;
  /** The in-flight attack's attack verb — see {@link import("./family").AttackMatch.verb}. */
  verb?: AttackVerb;
  weaponType?: string;
  mount?: string;
  firstAttackThisTurn?: boolean;
  /**
   * The **cover degree the TARGET stands in** for this attack — the same value the S2.5 cover
   * fold ({@link import("../resolve/attackModifiers").foldAttackCover}) hands the to-hit math,
   * threaded onto the event so {@link import("./family").AttackMatch.cover} can gate on it.
   * `"none"` is a real, set value (never absence), so a clause authoring `cover: "none"` matches
   * an unshielded target rather than reading as "the fact wasn't supplied."
   *
   * **Set wherever cover has been settled: the pre-roll target/attacker modifier fold
   * ({@link import("../resolve/attackModifiers").foldAttackModifiers}), the roll replacements'
   * facts, and both RESOLVED trigger fires (on_hit/on_crit, on_miss).
   * Absent at the DECLARED fire** (`fireAttackDeclared`), which runs once for the whole
   * declaration — before any per-target cover exists, and for an area attack over a set of
   * targets with different cover answers. A `cover` match on a `phase: "declared"` clause
   * therefore never fires; that is the honest answer, not an omission to fill in later.
   *
   * A melee attack, a tech attack, a Seeking weapon and a melee ACTION attack all skip the cover
   * fold entirely (RAW), so they read `"none"` here — which is what they are: not in cover.
   */
  cover?: CoverDegree;
  /**
   * The **weapon instance key** (`weaponInstanceKey`) of the resolving weapon. Set ONLY at
   * the RESOLVED fire sites (hit/crit + miss, `resolve/attackTriggerFires.ts`) — the identity fact the
   * `reserve_followup` atom reads (`ctx.event.weaponInstance`) to pin Executioner's follow-up
   * reservation to "the SAME weapon" that scored the hit. **Deliberately NOT a `match` field**
   * (`AttackMatch`, `family.ts`, does not expose it): an opaque instance key is never authored
   * in a pattern — it is payload carried for an ATOM to read, distinct from the
   * authored-matchable facts (`weaponType`/`mount`/…). It belongs on the typed event payload,
   * never as a flat `FireContext` field. Absent at the declared-phase fire and every non-attack
   * event — the atom skips (no reservation) when unset.
   */
  weaponInstance?: string;
  /**
   * The **composite granted-action id** (`<itemId>:<actionIndex>`, e.g. `"ms_markerlight:0"`)
   * the resolving attack fired as — the SAME value `resolve/weapons.ts`'s `attackHandlerId(intent,
   * fallbackWeaponId)` helper computes at each fire site. This is the field {@link
   * import("./family").AttackMatch.actionId} reads, mirroring {@link
   * import("./family").ActionMatch.actionId}'s `ActionEvent.actionId`.
   *
   * **Always SET, never absent, for any resolved attack**: `attackHandlerId` falls back to the
   * resolved weapon's OWN id for an ordinary weapon attack (never `undefined`), so a
   * `match.actionId: "ms_markerlight:0"` pattern on an ordinary `mw_assault_rifle` attack fails
   * on inequality (`"mw_assault_rifle" !== "ms_markerlight:0"`), not on absence. That is what
   * closes the over-grant where an equipped system's clause fires on every attack the carrier
   * makes — and it is the piece a hand-built fixture omitting `actionId` would not prove.
   */
  actionId?: string;
  /**
   * **How many characters this attack targets** — the area fan-out's own settled
   * `targetIds.length` (AFTER the per-target LoS exclusion, so it counts the characters that
   * actually resolve, not the raw template catch), or `1` on the single-target spine.
   *
   * The event-side twin of {@link import("./interpret").EffectContext.targetCount} (which the
   * `{TARGET_COUNT}` magnitude binding reads) — set from the SAME number at the SAME sites, so a
   * `match: { targetCount: { ">": 1 } }` gate and a `{ targetCount: "beyond_first" }` magnitude
   * on the same clause can never disagree.
   *
   * **Distinct from `areaAttack`** ({@link import("./interpret").EffectContext.areaAttack}),
   * which is a boolean about the RESOLUTION SHAPE and drives RAW's bonus-damage halving. A
   * one-target Blast is `areaAttack: true` with `targetCount: 1`.
   *
   * **Set at the RESOLVED fires and both attacker-side modifier folds; at the DECLARED fire it
   * is set only when the attack is NOT fanning out** (where the count is known to be 1). An
   * area attack's declaration happens BEFORE the template is enumerated and LoS-filtered, so
   * the count genuinely does not exist yet — the same honest absence {@link AttackEvent.cover}
   * documents, not an omission to fill in later. A `targetCount` match on a `phase: "declared"`
   * clause of an area attack therefore never fires.
   */
  targetCount?: number;
  /**
   * The **statuses this attack consumes** off its target: the `consumeOnHit` statuses (Lock On)
   * the wielder chose to consume, settled BEFORE the roll by `resolve/weaponConsume.ts`'s
   * `settleConsumeChoice` (RAW p.70: "may choose to gain +1 on that attack and then clear the
   * LOCK ON condition after that attack resolves"). Empty when the target carries none or the
   * wielder kept it. Read by {@link import("./family").AttackMatch.consumes}, so "an attack that
   * consumes Lock On" gates on the answer rather than on the target merely carrying the status.
   *
   * Set wherever `cover` is (the pre-roll modifier folds, the post-roll re-fold, both resolved
   * fires); absent at the DECLARED fire, which runs before the per-target choice exists.
   */
  consumes?: string[];
  /**
   * The **named reaction this attack spends**: `"covering_fire"` for Covering Fire's shot,
   * `"overwatch"` for any other reaction attack, absent for an attack that isn't a reaction. The
   * same answer the cost charges (`economy/coveringFire.ts`'s `reactionAttackName`). Read by
   * {@link import("./family").AttackMatch.reaction} (Heavy Gunner 2's "with the attack reaction
   * granted by that talent").
   *
   * Set at the declared fire and both resolved fires; the modifier folds don't carry it.
   */
  reaction?: string;
};

/**
 * The turn-family event payloads. No fields: the edge is the family name, and the carrier is
 * always the unit whose turn it is (see `TriggerFamily`'s doc).
 */
export type TurnEvent = { family: "start_turn" } | { family: "end_turn" };

/** The round-family event payloads, fired for every combatant. See {@link TurnEvent}. */
export type RoundEvent = { family: "start_round" } | { family: "end_round" };

/**
 * When a drone's aura acts on a character (`decisions/0201`):
 * - `turn_start` / `turn_end`: the character starts or ends its turn in the field.
 * - `enter`: the character's own movement (or a forced move) takes it into the field. Capped
 *   once per round per entry id (`aura_enter:<entryId>`).
 * - `arrive`: the field lands on the character: the drone is deployed, redeployed or moved.
 *   RAW "enter" is the character's move, so Hive's tick does not fire here; Lotus's reveal does.
 * - `before_attack`: the character in the field is about to make an attack, once per attack and
 *   before the roll (Sentinel). If the fire destroys it, the attack is not made.
 */
export type AuraMoment = "turn_start" | "enter" | "arrive" | "turn_end" | "before_attack";

/** The aura-family event payload: which {@link AuraMoment} the field is acting at. Set only by `resolveAuraTrigger`. */
export type AuraEvent = { family: "aura"; moment: AuraMoment };

/**
 * The action-family event payload — the taken action's catalog id (`"boost"`,
 * `"overcharge"`, `"ram"`, …). Set only at the action-taken fire site
 * ({@link import("../resolve").resolveActionTakenTrigger}).
 */
export type ActionEvent = {
  family: "action";
  actionId: string;
  /**
   * The RAW verb taken, before the ledger's collapse: a Lock On's `actionId` is `"quick_tech"`
   * (as are Scan, Bolster and Invade), and its `verb` is `"lock_on"`. Set by the intent fire site
   * when the intent is an earmark verb; absent elsewhere.
   */
  verb?: EarmarkVerb;
};

/**
 * The move-family event payload — the declared move's measured distance + endpoints, plus the
 * **committed anchor path** the mover actually travels. `from`/`to` are the declared endpoints;
 * nothing in the predicate `match` vocabulary reads any of the three spatial fields yet.
 *
 * **The two halves are NOT the same kind of fact**, and the difference is load-bearing:
 *  - **`distance` is declared** — `worldFor(state).distance(from, to)`, the
 *    straight-line measure between the checkpoint's endpoints. It is deliberately NOT the path's
 *    cost: `t_heavy_gunner:1`'s Covering Fire ("moved more than 1 space") is the only shipped
 *    consumer, and re-grounding its gate on traversed cost would silently change what that shipped
 *    content does to a mover walking around a corner. That is a content-semantics call nobody has
 *    made, so this field is left exactly as it was.
 *  - **`path` is a REAL mid-path fact, not a declaration.** It is the committed anchor sequence
 *    (`from` first, `to` last) from `@lancer-vtt/domain`'s `movePathAnchors` — the same
 *    `anchorPathTo` A* over the same `movementContext`/`moveBudgetFor` the `move_token` reducer
 *    itself walks for its engagement-stop check, so it is the route the mover genuinely takes, not
 *    an inference about one. Absent when the fire site had no declared move in scope, or the
 *    destination was not legally reachable (nothing to travel).
 *
 * Set only at `on_move`.
 */
export type MoveEvent = {
  family: "move";
  distance: number;
  from: Cell;
  to: Cell;
  /** The committed anchor path, `from` first and `to` last — see this type's doc. Absent when no path was in scope. */
  path?: readonly Cell[];
  /**
   * The mover chose this move: a move, Boost or reaction move through `request_move`, the one fire
   * site. A forced move (push, pull, Knockback) fires no move family at all. `false` is for a walk
   * someone else drives through the move pipeline (Puppet System, `ll1-invade-options` 6.1).
   */
  voluntary: boolean;
  /** `"declared"` at the interrupt window, before the move applies (`moveInterrupts.ts`); absent everywhere else. */
  phase?: "declared";
};

/**
 * The save-family event payload. The forced-save declaration fire (`fireForcedSaveDeclared`,
 * `interpret.ts`) constructs it off the forcing `force_save` atom's own `stat`/`dc`/`cause`
 * with `phase: "declared"`. The `phase: "resolved"` sibling has no fire site yet — the `"save"`
 * family isn't in `resolve.ts`'s `FIRED_TRIGGER_POINTS` for a resolved-phase fire.
 *
 * `cause` is a **free string** mirroring {@link import("./atoms").ForceSaveAtom.cause} exactly
 * (`"prone"`, `"knockback"`, an uncaused save omits it) — the actual authored vocabulary a
 * `force_save` atom's `cause` carries. A narrower enum couldn't represent
 * `ms_stable_structure`'s prone/knockback causes.
 */
export type SaveEvent = {
  family: "save";
  stat: string;
  cause?: string;
  dc: number;
  phase: "declared" | "resolved";
};

/**
 * The status-family event payload. Threaded at the status-lost fire site (`resolve.ts`'s
 * `resolveStatusLostHandlers`) — `change: "lost"` only; nothing fires a status-gained event
 * today, so `change: "gained"` is declared for family-completeness but never constructed.
 */
export type StatusEvent = {
  family: "status";
  change: "gained" | "lost";
  /** The status/condition name that was gained/lost (mirrors `AddStatusAtom.status`'s free-string vocabulary). */
  status: string;
  /** Why a status was lost (`event-engine-lifecycle` D5): its own duration ended it (`expired`), or something removed it (`removed`). */
  reason?: "expired" | "removed";
  /** The lost instance's `source`, when it had one. */
  source?: string;
};

/**
 * The damage-family event payload — `npc-features-fielded-roster` Phase 7.2. Built at two kinds
 * of site:
 *
 *   - **the attack interception window** (`gatherReaction`'s `compileAuthoredOffers`/
 *     `compileProtectorOffers`/`compileAutomaticOutcome`, `reactionRegistry.ts`), which always
 *     sets `ranged` from `ReactionContext.ranged` (`resolve/attack.ts`'s already-computed
 *     `rangedAttack`) — RAW's Rebound trigger ("you take damage from a ranged attack") is the
 *     motivating case;
 *   - **the non-attack damage notification site** (`resolve/triggers.ts`'s
 *     `resolveDamageTrigger`, `decisions/0175`), which leaves `ranged` UNSET.
 *
 * **`ranged` is optional because a burn tick has no ranged fact, and `false` would be a lie.**
 * `evaluateDamageMatch` compares by strict equality, so an unset `ranged` fails BOTH
 * `match: { ranged: true }` and `match: { ranged: false }` — the second being what an author
 * writes meaning "a melee or tech attack", which must not fire on burn. A `source` discriminator
 * (`"attack" | "burn" | "terrain" | …`) is the sanctioned shape if a clause ever needs "when you
 * take BURN damage"; reserved, not built.
 */
export type DamageEvent = {
  family: "damage";
  ranged?: boolean;
  /** A tech attack? Set and unset beside `ranged` (see {@link import("./family").DamageMatch.tech}). */
  tech?: boolean;
  /** Did the triggering attack hit? `false` for a Reliable floor on a miss; unset at the non-attack site (see {@link import("./family").DamageMatch.hit}). */
  hit?: boolean;
  /** Final damage above 0 after mitigation — set only by the settled-damage pass (see {@link import("./family").DamageMatch.dealt}). */
  dealt?: boolean;
  /** The damage's types (see {@link import("./family").DamageMatch.dtype}). */
  dtypes?: readonly DamageType[];
};

/** The typed per-family event payload a fired/folded `PredicateContext` carries. */
export type TriggerEvent = AttackEvent | TurnEvent | RoundEvent | ActionEvent | MoveEvent | SaveEvent | StatusEvent | DamageEvent | AuraEvent | HeatEvent | TrackDamageEvent;

/** The heat-family event: the carrier's heat went to 0 from above it (`referee/trackObserver.ts`). */
export type HeatEvent = { family: "heat"; cleared: boolean };

/** The track_damage-family event: the carrier lost `amount` of `track` (`referee/trackObserver.ts`). */
export type TrackDamageEvent = { family: "track_damage"; track: "structure" | "stress"; amount: number };

/**
 * Resolve a {@link PredicateActor}/AtomActorRef to a combatant id against the context.
 *
 * **`attacker` has NO `self` fallback.** An unbound `attacker`
 * (a clause firing at a family that never sets `actors.attacker` — every family except
 * `attack`/`damage`/`save`) resolves to `undefined`, which the closed predicate evaluator
 * already reads as a definite `false` and the atom interpreter reads as a skipped
 * recipient — never a crash. This is deliberately **asymmetric** with `target`, which
 * keeps its own fallback one level up (`fireTrigger`'s `primaryTarget ?? h.source`,
 * `triggerBus.ts`): `target` at a lifecycle family is a *designed* binding (the atom-
 * default contract in `resolveActor`, `interpret.ts`, rides it — an atom's omitted
 * `target` resolves to `ctx.actors.target ?? ctx.actors.self`), while `attacker` outside
 * an attack/damage/save has no such meaning — a convenience fallback there would let a clause
 * authored as "the *attacker*" silently read the carrier itself at any other family. The
 * family → bound-roles table this asymmetry is calibrated against is `coverage.ts`'s
 * `FAMILY_BOUND_ROLES`.
 */
export function resolveActorId(
  ref: string,
  actors: { self?: string; target?: string; attacker?: string; partner?: string },
): string | undefined {
  if (ref === "self") return actors.self;
  if (ref === "target") return actors.target;
  if (ref === "attacker") return actors.attacker;
  if (ref === "partner") return actors.partner;
  return ref; // a raw combatant id
}

/**
 * The status name RAW's Bonded rank 1 binds via ("choose another pilot ... to be your
 * Bondmate", `entries/statuses/bondmate.ts`). {@link bondmatePlantSteps} plants it at deploy.
 * Read by {@link bondmatePartner}; not exported — every consumer goes through these functions,
 * never a raw name-matched status scan of its own.
 */
const BONDMATE_STATUS_NAME = "Bondmate";

/**
 * The combatant `actorId`'s designated Bondmate, read off its own "Bondmate" status
 * instance's `partner` field — the SAME `StatusInstance.partner`
 * binding {@link import("../grapple").grapplePartner} reads for the Grapple relationship,
 * mirrored here for the Bonded one. A caller threads this onto the
 * pilot-effect gather's own `partner` kwarg (`gatherPilotModifiers`/`seedPilotModifiers`),
 * exactly as the attack folds (`resolve/attackModifiers.ts`, `resolve/toHit.ts`) already thread `grapplePartner` — so a
 * talent-authored `{who:"self", relativeTo:"partner"}`/`who:"partner"` clause resolves
 * against "who I am bonded to," the same shape a status-carried modifier gets for free via
 * `STATUS_VECTOR`'s own per-instance `partner` threading. `undefined` when the carrier holds
 * no "Bondmate" status (never bonded, or the marker was removed) — the caller then omits
 * `partner` entirely, so `ctx.actors.partner` stays unset (absent-means-false, same as every
 * other threaded fact here).
 */
export function bondmatePartner(state: EncounterState, actorId: string): string | undefined {
  return state.combatants[actorId]?.statuses.find((s) => s.name === BONDMATE_STATUS_NAME)?.partner;
}

/**
 * The `Bondmate` statuses the roster's pairings owe on this board (Bonded, Core p.90): one
 * `status_add` for each Bonded combatant (`t_bonded:N` frozen on it) with no Bondmate yet whose
 * roster Bondmate ({@link import("@lancer-vtt/domain").Combatant.bondmatePilotId}) is fielded
 * — the first live combatant whose `pilotId` matches, preferring a mech (it has a structure
 * track) over its on-foot pilot —
 * partnered to that combatant. Both halves of a mutual pair get one. Idempotent: a planted pair
 * owes nothing, so the referee runs it after every deploy.
 */
export function bondmatePlantSteps(state: EncounterState): StatusAddStep[] {
  const live = Object.values(state.combatants).filter((c) => !hasStatus(c.statuses, "Destroyed"));
  const steps: StatusAddStep[] = [];
  for (const c of live) {
    if (c.bondmatePilotId === undefined || !(c.base.pilotEffectIds ?? []).some((id) => id.startsWith("t_bonded:"))) continue;
    if (c.statuses.some((s) => s.name === BONDMATE_STATUS_NAME)) continue;
    const mates = live.filter((o) => o.id !== c.id && o.pilotId === c.bondmatePilotId);
    const mate = mates.find((o) => hasStructureTrack(o)) ?? mates[0];
    if (mate === undefined) continue;
    steps.push({ op: "status_add", target: c.id, status: BONDMATE_STATUS_NAME, expiry: { kind: "until_removed" }, partner: mate.id });
  }
  return steps;
}

/** Would deploying `combatant` into `state` plant a Bondmate ({@link bondmatePlantSteps})? The room hooks only those deploys. */
export function deployPlantsBondmate(state: EncounterState, combatant: Combatant): boolean {
  return bondmatePlantSteps({ ...state, combatants: { ...state.combatants, [combatant.id]: combatant } }).length > 0;
}

/**
 * The shared preamble of the pairwise ops (`hostile_to`, `adjacent_to`, `range_to`,
 * `is_closest_hostile`): resolve `who` and `relativeTo` and look both combatants up. `undefined`
 * when either is unresolved or absent, which each op reads as `false`.
 */
function resolvePair(
  pred: { who: PredicateActor; relativeTo: PredicateActor },
  ctx: PredicateContext,
): { whoId: string; relId: string; whoC: Combatant; relC: Combatant } | undefined {
  const whoId = resolveActorId(pred.who, ctx.actors);
  const relId = resolveActorId(pred.relativeTo, ctx.actors);
  const whoC = whoId ? ctx.state.combatants[whoId] : undefined;
  const relC = relId ? ctx.state.combatants[relId] : undefined;
  if (!whoId || !relId || !whoC || !relC) return undefined;
  return { whoId, relId, whoC, relC };
}

/** A drone (a non-actor character) or anything a system deployed, placed and not Destroyed; never a mine ({@link AdjacentToDeployedPredicate}). */
function isLiveDeployed(c: Combatant): boolean {
  if (c.pos == null || c.mineProfile !== undefined || hasStatus(c.statuses, "Destroyed")) return false;
  return (isCharacter(c) && !isActor(c)) || c.deployedFrom !== undefined;
}

/** One actor-ref field on a predicate node: where it sits (`who`, `relativeTo`, `source.actor`) and the ref it names. */
export type PredicateActorRefField = { field: string; ref: PredicateActor };

/**
 * The actor-ref fields ONE predicate node carries (non-recursive: `and`/`or`/`not` carry none of
 * their own). Read structurally off the field names every op uses for an actor (`who`,
 * `relativeTo`, `has_status`'s `source.actor` and `in_area`'s `source`), so a new op with a `who`
 * needs no edit here.
 * `lint.ts`'s binding rules and `actionRegistry.ts`'s `selfDecidablePredicate` both derive from
 * this instead of each keeping its own per-op switch.
 */
export function predicateActorRefFields(pred: Predicate): PredicateActorRefField[] {
  const out: PredicateActorRefField[] = [];
  if ("who" in pred) out.push({ field: "who", ref: pred.who });
  if ("relativeTo" in pred) out.push({ field: "relativeTo", ref: pred.relativeTo });
  if (pred.op === "has_status" && typeof pred.source === "object" && "actor" in pred.source) {
    out.push({ field: "source.actor", ref: pred.source.actor });
  }
  if (pred.op === "in_area") out.push({ field: "source", ref: pred.source });
  return out;
}

/** The `source` value a {@link StatusSourceSpec} requires, or `undefined` when it can't be known (reads `false`). */
function statusSourceId(spec: StatusSourceSpec, ctx: PredicateContext): string | undefined {
  if (spec === "carrier") return ctx.carrierSource;
  if ("id" in spec) return spec.id;
  return resolveActorId(spec.actor, ctx.actors);
}

/**
 * Evaluate a {@link Predicate} against a {@link PredicateContext}. Pure, total, and
 * **closed** — an unknown actor / absent resource / unanswered spatial fact resolves
 * to a definite boolean (no throw), so the interpreter never crashes on partial data.
 * Spatial predicates read `ctx.spatial[id]` (recorded input); a missing answer is `false`.
 */
export function evaluatePredicate(pred: Predicate, ctx: PredicateContext): boolean {
  switch (pred.op) {
    case "and":
      return pred.preds.every((p) => evaluatePredicate(p, ctx));
    case "or":
      return pred.preds.some((p) => evaluatePredicate(p, ctx));
    case "not":
      return !evaluatePredicate(pred.pred, ctx);
    case "has_status": {
      if (pred.scope === "any_live") {
        const sourceId = pred.source === undefined ? undefined : statusSourceId(pred.source, ctx);
        if (pred.source !== undefined && sourceId === undefined) return false;
        return Object.values(ctx.state.combatants).some(
          (c) => !hasStatus(c.statuses, "Destroyed") && c.statuses.some((s) => s.name === pred.status && (sourceId === undefined || s.source === sourceId)),
        );
      }
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      if (!c) return false;
      const turnOrdinal = ctx.state.activation.turnOrdinal;
      const appliedEarlier = (s: StatusInstance) => pred.appliedBefore === undefined || s.appliedTurn === undefined || s.appliedTurn !== turnOrdinal;
      if (pred.source === undefined) return c.statuses.some((s) => s.name === pred.status && appliedEarlier(s));
      const sourceId = statusSourceId(pred.source, ctx);
      if (sourceId === undefined) return false;
      return c.statuses.some((s) => s.name === pred.status && s.source === sourceId && appliedEarlier(s));
    }
    case "has_effect_id": {
      const id = resolveActorId(pred.who, ctx.actors);
      return combatantEffectIds(id ? ctx.state.combatants[id] : undefined).includes(pred.id);
    }
    case "has_capability": {
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      if (!c) return false;
      return CAPABILITY_PREDICATES[pred.capability](c);
    }
    case "resource": {
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      if (!c) return false;
      const cur = c.current.resources?.[pred.key] ?? 0;
      return compare(cur, pred.cmp, pred.value);
    }
    case "limited_charges": {
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      const pool = c ? heldLimitedPool(c, pred.items) : undefined;
      return pool !== undefined && compare(pool.left, pred.cmp, pred.value);
    }
    case "cap_open": {
      const id = resolveActorId(pred.who, ctx.actors);
      const on = pred.on === undefined ? undefined : resolveActorId(pred.on, ctx.actors);
      if (!id || (pred.on !== undefined && !on)) return false;
      return affordable({ kind: "resource_cap_use", carrier: id, key: resourceCapRawKey(pred.cap, on ?? undefined), max: pred.cap.max }, ctx.state, ctx.costs);
    }
    case "stat": {
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      if (!c) return false;
      const v = readStat(c, pred.stat);
      if (v === null) return false;
      return compare(v, pred.cmp, pred.value);
    }
    case "gm_ruling":
      return gmRuling(pred, ctx);
    case "attacker_is": {
      const a = resolveActorId("attacker", ctx.actors);
      const w = resolveActorId(pred.who, ctx.actors);
      return a !== undefined && w !== undefined && a === w;
    }
    case "target_is": {
      const t = resolveActorId("target", ctx.actors);
      const w = resolveActorId(pred.who, ctx.actors);
      return t !== undefined && w !== undefined && t === w;
    }
    case "loadout_has_weapon_type": {
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      if (!c) return false;
      return (c.base.weapons ?? []).some((w) => w.type === pred.value);
    }
    case "exists_ally": {
      const whoId = resolveActorId(pred.who, ctx.actors);
      const relId = resolveActorId(pred.relativeTo, ctx.actors);
      if (!whoId || !relId) return false;
      const whoC = ctx.state.combatants[whoId];
      if (!whoC) return false;
      const side = sideOf(whoC);
      for (const id of Object.keys(ctx.state.combatants)) {
        if (id === whoId) continue; // never your own ally
        const candidate = ctx.state.combatants[id]!;
        if (pred.among === "character" ? !isCharacter(candidate) : sideOf(candidate) !== side) continue;
        const d = combatantRange(ctx.state, id, relId);
        if (d === null) continue; // unplaced — no map answer, this candidate can't satisfy it
        if (pred.rel === "adjacent" ? d === 1 : pred.param !== undefined && d <= pred.param) return true;
      }
      return false;
    }
    case "danger_zone": {
      const id = resolveActorId(pred.who, ctx.actors);
      const c = id ? ctx.state.combatants[id] : undefined;
      if (!c) return false;
      const heat = c.current.heat ?? null;
      const maxHeat = c.base.maxHeat ?? null;
      if (heat === null || maxHeat === null || maxHeat <= 0) return false;
      return heat >= maxHeat / 2;
    }
    case "on_turn": {
      const id = resolveActorId(pred.who, ctx.actors);
      if (!id) return false;
      const turn = ctx.state.activation.turn;
      return turn.phase !== "acting" || turn.actor === id;
    }
    case "collided": {
      const id = resolveActorId(pred.who, ctx.actors);
      return (ctx.collisions ?? []).some((c) => c.mover === id && (pred.with === "obstruction" ? c.obstructed : c.occupants.length > 0));
    }
    case "moved_this_turn": {
      const id = resolveActorId(pred.who, ctx.actors);
      const turn = ctx.state.activation.turn;
      return id !== undefined && turn.phase === "acting" && turn.actor === id && spentMove(turn.budget) > 0;
    }
    case "acted_this_turn": {
      const id = resolveActorId(pred.who, ctx.actors);
      const turn = ctx.state.activation.turn;
      if (id === undefined || turn.phase !== "acting" || turn.actor !== id) return false;
      const used = (ledger: Record<string, number> | undefined) => Object.values(ledger ?? {}).some((n) => n > 0);
      return !turnBudgetVirgin(turn.budget) || used(turn.budget.actionUses) || used(turn.budget.protocolUses);
    }
    case "scene_environment":
      return encounterEnvironment(ctx.state) === pred.value;
    case "hostile_to": {
      const pair = resolvePair(pred, ctx);
      return pair !== undefined && sideOf(pair.whoC) !== sideOf(pair.relC);
    }
    case "adjacent_to": {
      const pair = resolvePair(pred, ctx);
      return pair !== undefined && combatantRange(ctx.state, pair.whoId, pair.relId) === 1;
    }
    case "adjacent_to_deployed": {
      const id = resolveActorId(pred.who, ctx.actors);
      if (id === undefined) return false;
      return Object.values(ctx.state.combatants).some((d) => isLiveDeployed(d) && combatantRange(ctx.state, id, d.id) === 1);
    }
    case "size_vs": {
      const pair = resolvePair(pred, ctx);
      return pair !== undefined && compare(pair.whoC.base.size, pred.cmp, pair.relC.base.size);
    }
    case "can_swap": {
      const pair = resolvePair(pred, ctx);
      return pair !== undefined && swapFits(ctx.state, { a: pair.whoId, b: pair.relId });
    }
    case "line_of_sight": {
      const pair = resolvePair(pred, ctx);
      return pair !== undefined && combatantLineOfSight(ctx.state, pair.whoId, pair.relId);
    }
    case "range_to": {
      const pair = resolvePair(pred, ctx);
      if (!pair) return false;
      const d = combatantRange(ctx.state, pair.whoId, pair.relId);
      if (d === null) return false;
      const max = pred.max === "sensors" ? pair.relC.base.sensors : pred.max;
      if (pred.max === "sensors" && max === undefined) return false;
      if (max !== undefined && d > max) return false;
      if (pred.min !== undefined && d < pred.min) return false;
      return true;
    }
    case "is_closest_hostile": {
      const pair = resolvePair(pred, ctx);
      if (!pair) return false;
      const { whoId, relId, whoC, relC } = pair;
      if (sideOf(whoC) === sideOf(relC)) return false; // `who` must itself be hostile to `relativeTo`
      if (!isCharacter(whoC)) return false; // RAW "closest hostile CHARACTER" — a mine or object never qualifies
      const side = sideOf(relC);
      let minDist: number | null = null;
      let countAtMin = 0;
      for (const id of Object.keys(ctx.state.combatants)) {
        const candidate = ctx.state.combatants[id]!;
        if (sideOf(candidate) === side) continue; // only candidates hostile to `relativeTo`
        if (!isCharacter(candidate)) continue; // a hostile deployable/object is not a character and can't be "closest"
        const d = combatantRange(ctx.state, id, relId);
        if (d === null) continue; // unplaced — no map answer, can't be "closest"
        if (minDist === null || d < minDist) {
          minDist = d;
          countAtMin = 1;
        } else if (d === minDist) {
          countAtMin += 1;
        }
      }
      if (minDist === null) return false; // no hostile with a measurable position
      const whoDist = combatantRange(ctx.state, whoId, relId);
      if (whoDist === null || whoDist !== minDist) return false;
      if (pred.unique && countAtMin > 1) return false; // a tie at the minimum ⇒ false for everyone
      return true;
    }
    case "in_area": {
      const whoId = resolveActorId(pred.who, ctx.actors);
      const sourceId = resolveActorId(pred.source, ctx.actors);
      const whoC = whoId ? ctx.state.combatants[whoId] : undefined;
      if (!whoC || sourceId === undefined) return false;
      const world = worldFor(ctx.state);
      const cells = footprintCells(world, whoC);
      return Object.values(ctx.state.areaEffects ?? {}).some(
        (area) => area.source === sourceId && (pred.grants === undefined || area.grants === pred.grants) && areaHolds(world, area, cells),
      );
    }
    case "object_group_deployed": {
      const whoId = resolveActorId(pred.who, ctx.actors);
      if (!whoId) return false;
      return objectGroupOut(ctx.state, `${whoId}:${pred.groupId}`, pred.live);
    }
    case "chose":
      return ctx.chosen?.[pred.key] === pred.value;
  }
}

/** Read a numeric stat off a combatant (frozen base or tracked current); `null` if absent. */
function readStat(
  c: EncounterState["combatants"][string],
  stat: StatPredicate["stat"],
): number | null {
  switch (stat) {
    case "hp":
      return c.current.hp;
    case "heat":
      return c.current.heat ?? null;
    case "maxHp":
      return c.base.maxHp;
    case "maxHeat":
      return c.base.maxHeat ?? null;
    case "armor":
      return c.base.armor;
    case "evasion":
      return c.base.evasion;
    case "edef":
      return c.base.edef;
    case "speed":
      return c.base.speed;
    case "saveTarget":
      return c.base.saveTarget ?? null;
    case "size":
      return c.base.size;
  }
}

/** The decider id a {@link GmRulingPredicate}'s halt names: no combatant, so the room routes the prompt to GM slots only. */
export const GM_DECIDER = "gm";

const GM_RULING_OPTIONS = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];

/** The prompt id a {@link GmRulingPredicate}'s answer is recorded under. */
export function gmRulingPromptId(id: string): string {
  return `gm_ruling:${id}`;
}

function gmRuling(pred: GmRulingPredicate, ctx: PredicateContext): boolean {
  const promptId = gmRulingPromptId(pred.id);
  const answer = ctx.rulings?.choose(promptId, GM_RULING_OPTIONS.map((o) => o.id));
  if (answer !== undefined) return answer === "yes";
  if (ctx.rulings?.requireAnswer) {
    throw new NeedsDecisionSignal(promptId, gmRulingPrompt(pred.prompt, ctx), { options: GM_RULING_OPTIONS, who: GM_DECIDER });
  }
  return false;
}

/** `prompt` with each `{self}`/`{target}`/`{attacker}`/`{partner}` replaced by that combatant's name (the id if unnamed, the token itself if unbound). */
export function gmRulingPrompt(prompt: string, ctx: Pick<PredicateContext, "state" | "actors">): string {
  return prompt.replace(/\{(self|target|attacker|partner)\}/g, (token, ref: PredicateActor) => {
    const id = resolveActorId(ref, ctx.actors);
    if (id === undefined) return token;
    return ctx.state.combatants[id]?.name ?? id;
  });
}

// re-export for callers that build contexts from a name list
export { statusNames };
